import { Grid, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ItemCategoryProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Itemscategory } from "../../assets/SideBarIcons/items-category.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { useEffect, useState } from "react";
import Loader from "layout/Loader";
import { GetItemCategoryByIdResponse } from "subModule/src/services/itemcategory/interface";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";

interface FormData {
  [key: string]: any;
}
export default function ItemCategoryModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
  } = useForm<FormData>({ defaultValues: {} });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedItemCategoryPlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phItemCategoryAr") : t("phItemCategoryEn");
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "Items",
    moduleLabelKey: "itemsCategory",
    moduleHref: "/part-categories",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    ItemCategoryProfileService.getItemCategoryById(+(params as any)?.id)
      .then((res: GetItemCategoryByIdResponse) => {
        if (res.success) {
          reset({ ...(res?.data || {}) });
          captureLabel(res?.data?.title);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    if (params?.id) {
      ItemCategoryProfileService.updateItemCategory(+(params as any)?.id, {
        ...data,
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.title);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("itemCategory") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
          setIsBtnEnabled(true);
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      ItemCategoryProfileService.createItemCategory(data)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("itemCategory") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("editItemCatagory") : t("addItemCatagory")}
        icon={<Itemscategory height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_ITEMCATEGORY_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={params?.id ? "editItemCatagory" : "addItemCatagory"}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={params?.id ? "update" : "submit"}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={handleSubmit(onSubmit)}
          formSubmit={handleSubmit(onSubmit)}
          content={
            <Grid
              container
              component="form"
              id="form"
              className="items-baseline"
              spacing={3}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("title", {
                    validate: {
                      required: (value) =>
                        value.trim() !== "" || t("This field is required."),
                    },
                  })}
                  label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                  placeholder={localizedItemCategoryPlaceholder(
                    primaryLanguage,
                  )}
                  title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                    .replace(/\s+/g, " ")
                    .trim()}
                  fullWidth
                  error={!!errors.title}
                  helperText={errors.title?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("secondaryTitle")}
                  label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                  placeholder={localizedItemCategoryPlaceholder(
                    secondaryLanguage,
                  )}
                  title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                    .replace(/\s+/g, " ")
                    .trim()}
                  fullWidth
                  error={!!errors.secondaryTitle}
                  helperText={errors.secondaryTitle?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  {...register("categoryCode", {
                    validate: {
                      required: (value) =>
                        value?.trim() !== "" || t("This field is required."),
                    },
                  })}
                  label={t("categoryCode") + " *"}
                  placeholder={t("phItemCategoryCode")}
                  title={t("categoryCode")}
                  fullWidth
                  error={!!errors.categoryCode}
                  helperText={errors.categoryCode?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          }
        />
      )}
    </>
  );
}
